import { Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { interval } from 'rxjs';
import { MenuStateModel } from './models/menu/menu-state.model';
import { UtilisateurCourantModel } from './models/utilisateurs/utilisateur-courant.model';
import { TachesPopupControlService } from './pages/gestion-taches/taches-popup-control.service';
import { ConfigService } from './services/config/config.service';
import { ConsentementService } from './services/consentement/consentement.service';
import { CookiesService } from './services/cookies.service';
import { MenuService } from './services/menu/menu.service';
import { UtilisateurCourantService } from './services/utilisateurs/utilisateur-courant.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
  title = 'EParcoursFront';
  private destroyRef = inject(DestroyRef);
  private utilisateurCourantService = inject(UtilisateurCourantService);
  private tachesControlService = inject(TachesPopupControlService);
  private configService = inject(ConfigService);
  private cookieService = inject(CookiesService);
  private menuService = inject(MenuService);
  private consentementService = inject(ConsentementService);

  readonly user = signal<UtilisateurCourantModel | null>(null);
  readonly userIsLogguedIn = computed(() => this.user() != null);
  readonly tacheEditee = computed(() => this.tachesControlService.popupUpsert()?.tache ?? null);
  readonly isUpsertTacheOnly = computed(() => this.tachesControlService.popupUpsert()?.isUpsertOnly ?? false);
  readonly isPanneauTachesVisible = computed(
    () => this.userIsLogguedIn() && this.tachesControlService.isPopupOpen(),
  );

  menuState: MenuStateModel = {
    isMenuMobileOpened: false,
    isReduced: false,
  };
  public updateAvailable = false;
  public version: string;

  constructor() {
    // Avant toute lecture : allège tout de suite les en-têtes des anciens cookies de préférences.
    this.cookieService.migrerCookiesVersLocalStorage();
    this.destroyRef.onDestroy(() => this.fermerTachesPanel());
  }

  ngOnInit(): void {
    this.consentementService.initialiser();
    this.checkAppVersion();

    this.utilisateurCourantService.utilisateurCourantEvent.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe({
      next: user => {
        this.user.set(user);
        this.checkAppVersion();
      },
    });

    interval(60 * 60 * 1000).pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => this.checkAppVersion());
  }

  isErrorReportAvailable() {
    return true;
  }

  fermerTachesPanel() {
    this.tachesControlService.masquerPopup();
  }

  public checkAppVersion() {
    this.configService.loadVersionFromJson().pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe({
      next: (version) => {
        const currentVersion = version.version;
        this.version = currentVersion;
        const stored = this.cookieService.getCookie('version');
        let storedVersion: string | null = null;

        try {
          storedVersion = JSON.parse(stored)?.version;
          this.menuService.version = storedVersion;
        } catch {
          storedVersion = null;
        }
        if (currentVersion !== storedVersion) {
          this.clearLocalStorage();
          if (!this.userIsLogguedIn()) {
            this.cookieService.setCookie('version', { version: currentVersion });
            this.reloadApp();
          } else {
            this.menuService.newVersion = true;
          }
        }
      },
    });
  }

  public reloadApp() {
    this.clearLocalStorage();
    this.cookieService.setCookie('version', { version: this.version });
    this.cookieService.forceReload(this.version);
  }

  private clearLocalStorage(): void {
    this.cookieService.viderLocalStorageSaufPreferences();
  }
}
